import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import {
  orderSelectors,
  getOrders,
  getOrderById,
  createOrder,
  updateOrder,
  deleteOrder,
  completeOrder,
  OrderListItem,
} from 'entities/order';
import { userSelectors } from 'entities/user';
import { OrderRequestDto } from 'shared/api';
import { useGlobalSpinner } from 'shared/hooks';
import { saveFile } from 'shared/utils/files';
import { attachmentApi } from '../../../entities/attachment/api/attachmentApi';
import { ORDER_STATUS_CONFIG, WORK_TYPE_CONFIG } from '../constants/orderConstants';

export const useOrders = () => {
  const dispatch = useAppDispatch();
  const orders = useAppSelector(orderSelectors.getOrders);
  const isLoading = useAppSelector(orderSelectors.getOrdersLoading);
  const orderDetail = useAppSelector(orderSelectors.getOrderDetail);
  const orderDetailLoading = useAppSelector(orderSelectors.getOrderDetailLoading);
  const hasAdminRole = useAppSelector(userSelectors.hasAdminRole);
  const user = useAppSelector(userSelectors.getUser);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const loadOrders = useCallback(() => {
    showSpinner('Загрузка заказов...');
    const params = hasAdminRole ? {} : (user?.id ? { userId: user.id } : {});
    dispatch(getOrders(params)).finally(() => {
      hideSpinner();
    });
  }, [dispatch, showSpinner, hideSpinner, hasAdminRole, user]);

  const createNewOrder = useCallback(async (args: { data: OrderRequestDto; onSuccess?: () => void }) => {
    try {
      showSpinner('Создание заказа...');
      await dispatch(createOrder(args.data)).unwrap();
      loadOrders();
      args.onSuccess?.();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadOrders, showSpinner, hideSpinner]);

  const updateExistingOrder = useCallback(async (args: { id: number; data: OrderRequestDto; onSuccess?: () => void }) => {
    try {
      showSpinner('Обновление заказа...');
      await dispatch(updateOrder({ id: args.id, data: args.data })).unwrap();
      args.onSuccess();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadOrders, showSpinner, hideSpinner]);

  const loadOrderDetail = useCallback(async (id: number) => {
    try {
      showSpinner('Загрузка данных заказа...');
      const result = await dispatch(getOrderById(id)).unwrap();
      return { success: true, detail: result } as const;
    } catch (error) {
      return { success: false, error } as const;
    } finally {
      hideSpinner();
    }
  }, [dispatch, getOrderById, showSpinner, hideSpinner]);

  const deleteOrderById = useCallback(async (id: number) => {
    try {
      showSpinner('Удаление заказа...');
      await dispatch(deleteOrder(id)).unwrap();
      loadOrders();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadOrders, showSpinner, hideSpinner]);

  const completeOrderById = useCallback(async (id: number, reportFileUuid: string) => {
    try {
      showSpinner('Завершение заказа...');
      await dispatch(completeOrder({ id, reportFileUuid })).unwrap();
      loadOrders();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadOrders, showSpinner, hideSpinner]);

  const getStatusInfo = useCallback((order: OrderListItem) => {
    const status = order.status as keyof typeof ORDER_STATUS_CONFIG;
    return ORDER_STATUS_CONFIG[status] || ORDER_STATUS_CONFIG.NEW;
  }, []);

  const getWorkTypeInfo = useCallback((order: OrderListItem) => {
    const workType = order.workType as keyof typeof WORK_TYPE_CONFIG;
    return WORK_TYPE_CONFIG[workType] || WORK_TYPE_CONFIG.MAINTENANCE;
  }, []);

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  }, []);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU');
  }, []);

  const truncateText = useCallback((text: string, maxLength: number = 50) => {
    if (!text) return 'Нет комментария';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  }, []);

  const downloadAttachment = useCallback(async (attachmentUuid: string, filename?: string) => {
    try {
      showSpinner('Получение ссылки для скачивания...');
      const response = await attachmentApi.getDownloadUrl(attachmentUuid);

      if (!response.downloadUrl) {
        throw new Error('Не удалось получить ссылку для скачивания');
      }

      // Используем существующую утилиту для скачивания
      await saveFile(response.downloadUrl, filename || `report_${attachmentUuid}`);

      return { success: true };
    } catch (error) {
      console.error('Ошибка скачивания файла:', error);
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [showSpinner, hideSpinner]);

  return useMemo(() => ({
    orders,
    isLoading,
    loadOrders,
    createNewOrder,
    updateExistingOrder,
    deleteOrderById,
    completeOrderById,
    downloadAttachment,
    orderDetail,
    orderDetailLoading,
    loadOrderDetail,
    getStatusInfo,
    getWorkTypeInfo,
    formatDate,
    formatDateTime,
    truncateText,
  }), [
    orders,
    isLoading,
    loadOrders,
    createNewOrder,
    updateExistingOrder,
    deleteOrderById,
    completeOrderById,
    downloadAttachment,
    orderDetail,
    orderDetailLoading,
    loadOrderDetail,
    getStatusInfo,
    getWorkTypeInfo,
    formatDate,
    formatDateTime,
    truncateText,
  ]);
};
